import * as THREE from 'three';

import { useRef, useState } from 'react';

import { useFrame } from '@react-three/fiber';

type CubeLogoProps = {
  position: [number, number, number];
  scale?: number;
  rotation?: [number, number, number];
};

export function CubeLogo({
  position = [0, 0, 0],
  scale = 1,
  rotation = [0, 0, 0],
}: CubeLogoProps) {
  const cubeRef = useRef<THREE.Group>(null);
  const [hover, setHover] = useState(false);

  // Subtle animation
  useFrame((state) => {
    if (cubeRef.current) {
      // Float up and down slightly
      cubeRef.current.position.y =
        Math.sin(state.clock.getElapsedTime() * 0.5) * 0.1;

      // Gentle rotation
      cubeRef.current.rotation.y += 0.003;
    }
  });

  return (
    <group
      position={position}
      rotation={rotation}
      scale={scale}
      onPointerOver={() => setHover(true)}
      onPointerOut={() => setHover(false)}
    >
      {/* The cube logo with animated floating */}
      <group ref={cubeRef} position={[0, 0.6, 0]} scale={hover ? 1.1 : 1}>
        {/* Outer cube with transparent faces */}
        <mesh castShadow>
          <boxGeometry args={[0.8, 0.8, 0.8]} />
          <meshStandardMaterial
            attach="material-0"
            color="#333333"
            transparent
            opacity={0.2}
            side={THREE.DoubleSide}
          />
          <meshStandardMaterial
            attach="material-1"
            color="#333333"
            transparent
            opacity={0.2}
            side={THREE.DoubleSide}
          />
          <meshStandardMaterial
            attach="material-2"
            color="#FFFFFF"
            transparent
            opacity={0.2}
            side={THREE.DoubleSide}
          />
          <meshStandardMaterial
            attach="material-3"
            color="#333333"
            transparent
            opacity={0.2}
            side={THREE.DoubleSide}
          />
          <meshStandardMaterial
            attach="material-4"
            color="#333333"
            transparent
            opacity={0.5}
            side={THREE.DoubleSide}
          />
          <meshStandardMaterial
            attach="material-5"
            transparent
            opacity={0}
            side={THREE.DoubleSide}
          />
        </mesh>

        {/* Interior diagonal planes for the pyramid effect */}
        <group rotation={[0, Math.PI / 4, 0]}>
          {/* First diagonal plane */}
          <mesh position={[0, 0, 0]}>
            <planeGeometry args={[0.8, 0.8]} />
            <meshStandardMaterial
              color="#000000"
              side={THREE.DoubleSide}
              flatShading
            />
          </mesh>

          {/* Second diagonal plane (perpendicular) */}
          <mesh position={[0, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
            <planeGeometry args={[0.8, 0.8]} />
            <meshStandardMaterial
              color="#000000"
              side={THREE.DoubleSide}
              flatShading
              opacity={0.5}
            />
          </mesh>
        </group>

        {/* Interior diagonal plane for the corner split */}
        <mesh position={[0, 0, 0]} rotation={[Math.PI / 4, 0, Math.PI / 4]}>
          <planeGeometry args={[1.13, 1.13]} /> {/* √2 to reach corners */}
          <meshStandardMaterial
            color="#111111"
            side={THREE.DoubleSide}
            flatShading
            opacity={0.5}
          />
        </mesh>

        {/* Triangular cursor shape placed diagonally inside the cube */}
        <mesh
          position={[0.05, 0, 0.05]}
          rotation={[Math.PI / 5, Math.PI / 5, 0]}
        >
          <coneGeometry args={[0.25, 0.5, 3]} />{' '}
          {/* 3-sided cone to make a triangle */}
          <meshStandardMaterial
            color="#111111"
            emissive="#444444"
            emissiveIntensity={0.3}
            flatShading
            opacity={0.5}
          />
        </mesh>

        {/* Add a small cylinder to represent the cursor "button" */}
        <mesh
          position={[0.05, 0, 0.05]}
          rotation={[Math.PI / 5, Math.PI / 5, 0]}
        >
          <cylinderGeometry args={[0.1, 0.1, 0.05, 8]} />
          <meshStandardMaterial
            color="#080808"
            emissive="#333333"
            emissiveIntensity={0.3}
            flatShading
            opacity={0.5}
          />
        </mesh>
      </group>
    </group>
  );
}
